// ========================================
// SOCKET.IO CLIENT SERVICE
// Real-time synchronization for concurrent users
// ========================================

import { io } from 'socket.io-client';

let socket = null;
const eventListeners = new Map();

/**
 * Connect to the Socket.IO server
 * @param {string} token - JWT ID token from MSAL for authentication
 * @returns {Object} Socket instance
 */
export const connectSocket = (token) => {
    if (socket?.connected) {
        return socket;
    }

    // Connect to the backend server (same origin in production, or backend URL in dev)
    const serverUrl = process.env.NODE_ENV === 'production'
        ? window.location.origin
        : 'http://localhost:3001';

    socket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        auth: { token }
    });

    socket.on('connect', () => {
        console.log('[Socket] Connected:', socket.id);
    });

    socket.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected:', reason);
    });

    socket.on('connect_error', (error) => {
        console.warn('[Socket] Connection error:', error.message);
    });

    return socket;
};

/**
 * Disconnect from the Socket.IO server
 */
export const disconnectSocket = () => {
    if (socket) {
        socket.disconnect();
        socket = null;
        eventListeners.clear();
    }
};

/**
 * Subscribe to a socket event
 * @param {string} event - Event name
 * @param {Function} callback - Event handler
 * @returns {Function} Unsubscribe function
 */
export const onSocketEvent = (event, callback) => {
    if (!socket) {
        console.warn('[Socket] Not connected. Call connectSocket() first.');
        return () => { };
    }

    socket.on(event, callback);

    // Track listener for cleanup
    if (!eventListeners.has(event)) {
        eventListeners.set(event, new Set());
    }
    eventListeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
        socket?.off(event, callback);
        eventListeners.get(event)?.delete(callback);
    };
};

/**
 * Remove all listeners for a specific event
 * @param {string} event - Event name
 */
export const offSocketEvent = (event) => {
    if (socket && eventListeners.has(event)) {
        eventListeners.get(event).forEach(callback => {
            socket.off(event, callback);
        });
        eventListeners.delete(event);
    }
};

/**
 * Update the auth token used for the next reconnect attempt.
 * Call this before each reconnect so an expired MSAL token is not reused.
 * @param {string} token - Fresh JWT ID token
 */
export const setSocketToken = (token) => {
    if (socket) {
        socket.auth = { token };
    }
};

/**
 * Check if socket is connected
 * @returns {boolean} Connection status
 */
export const isSocketConnected = () => {
    return socket?.connected || false;
};

/**
 * Get the socket instance
 * @returns {Object|null} Socket instance
 */
export const getSocket = () => socket;

// ========================================
// EVENT CONSTANTS
// ========================================

export const SOCKET_EVENTS = {
    // Audit events
    AUDIT_NEW: 'audit:new',

    // User events
    USER_DISABLED: 'user:disabled',
    USER_ENABLED: 'user:enabled',

    // Team events
    TEAM_BLOCKED: 'team:blocked',
    TEAM_UNBLOCKED: 'team:unblocked',
    TEAM_UPDATED: 'team:updated',

    // Budget events
    BUDGET_UPDATED: 'budget:updated',

    // Webhook events
    WEBHOOK_UPDATED: 'webhook:updated',

    // Feedback events
    FEEDBACK_NEW: 'feedback:new',

    // Generic data change
    DATA_CHANGED: 'data:changed'
};
